import React, { useContext, useEffect, useState, useCallback } from "react";
import { message } from "antd";
import RecipientDetailsModal from "../payments/recipient/fullDetails";
import EditRecipientButton from "../payments/recipient/editRecipient";
import { backendDeleteWithoutRefresh } from "../../../helpers/callBackend";
import axiosInstance from "../../../helpers/axios";
import { PaymentsContext } from "../../../helpers/contexts/PaymentsContext";
import TableWithLoadingWrapper from "../../generic/dataGrid";
import GenericDropdown from "../../generic/genericDropdown";
import GenericDeleteModal from "../../buttons/genericDeleteModal";
import checkFlag from "../../../helpers/featureFlags"
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RecipientPaymentsActions from "./recipient/RecipientPaymentsActions";

function RecipientsTable(props) {
  const { dispatch, state } = useContext(PaymentsContext);
  // TODO: Shubham, this is using quick state rather than full recipient details here, causing the page to error
  const [accountHolderFilter, setAccountHolderFilter] = useState([]);
  const [bankNameFilter, setBankNameFilter] = useState([]);

  const fetchFilters = useCallback(async () => {
    const res = await axiosInstance.get("/api/payments/counterparty/");
    var data = res.data.data;
    var accountHolderFilter = [], bankNameFilter = [];
    var t1 = [], t2 = [];
    for (var i = 0; i < data.length; i++) {
      var name = data[i].retrieved_values.name, bankName = data[i].retrieved_values.bank?.name;
      if (!t1.includes(name.toLowerCase())) {
        accountHolderFilter.push(
          {
            text: name,
            value: name.toLowerCase(),
          }
        );
        t1.push(name.toLowerCase());
      }
      if (!t2.includes(bankName.toLowerCase())) {
        bankNameFilter.push(
          {
            text: bankName,
            value: bankName.toLowerCase(),
          }
        );
        t2.push(bankName.toLowerCase());
      }
    }
    setAccountHolderFilter(accountHolderFilter);
    setBankNameFilter(bankNameFilter);
  }, []);

  useEffect(() => {
    fetchFilters();
  }, [dispatch, fetchFilters]);

  useEffect(() => {
    if (!props.loading) {
      dispatch({
        type: "FETCH_RECIPIENTS",
        payload: { recipients: props.recipientsData },
      });
    }
  }, [dispatch, props.loading, props.recipientsData]);

  const accountHolderRender = (name, nickname) => {
    if (name === nickname || nickname === undefined || nickname === '') {
      return name
    }
    return `${name} (${nickname})`
  }

  const handleDeleteRecipient = (RecipientId) => {
    dispatch({
      type: "REQUEST_START",
    });

    backendDeleteWithoutRefresh("/api/payments/counterparty/", { counterpartyId: RecipientId })
      .then((response) => {
        message.success("Recipient deleted")
        dispatch({
          type: "DELETE_RECIPIENT",
          payload: { recipientId: RecipientId },
        });
        props.onSuccessFuncDelete(RecipientId);
        props.setRecipients((prev) => prev.filter((recipient) => {
          return !(recipient.id === RecipientId)
        }));
        props.setShowRecipients((prev) => prev.filter((recipient) => {
          return !(recipient.id === RecipientId)
        }));
      })
      .catch((error) => {
        console.error(error);
        dispatch({
          type: "REQUEST_END",
        });
      });
  };

  const columns = [
    {
      field: "retrieved_values",
      headerName: "Account Holder",
      flex: 1,
      filterable: true,
      sortable: true,
      valueGetter: (params) => {
        return params?.name;
      },
      sortComparator: (a, b) => {
        return a?.localeCompare(b);
      },
      renderCell: (params) => (
        <p>{accountHolderRender(params?.row?.retrieved_values?.name, params?.row?.retrieved_values?.nickname)}</p>
      ),
    },
    {
      field: "bank",
      headerName: "Bank",
      flex: 1,
      filterable: true,
      sortable: true,
      valueGetter: (a, params) => {
        return params?.retrieved_values?.bank?.name
      },
      sortComparator: (a, b) => a?.localeCompare(b),
      renderCell: (params) => (
        <p>{params?.row?.retrieved_values?.bank?.name}</p>
      ),
    },
    {
      field: "bankDetails",
      headerName: "Bank Details",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
          return(
            <RecipientDetailsModal recipientDetails={params.row} />
          )
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.1,
      sortable: false,
      filterable: false,
      align: "right",
      renderCell: (params) => (
        <GenericDropdown dropdownItems={[
          <EditRecipientButton
            onSuccessFuncEdit={props.onSuccessFuncEdit}
            data={params.row}
            setRecipients={props.setRecipients}
            setShowRecipients={props.setShowRecipients} />,
          <GenericDeleteModal
            type="Recipient"
            title={`Are you sure you want to delete ${accountHolderRender(params.row.retrieved_values.name, params.row.retrieved_values.nickname)}?`}
            description={`By clicking Delete below you will no longer have the recipient ${accountHolderRender(params.row.retrieved_values.name, params.row.retrieved_values.nickname)} on your dashboard. If you do not want to proceed or unsure, please click X on the upper right.`}
            handleDeleteSuccess={() => handleDeleteRecipient(params.row.id)}
            handleDeleteFailure={() => message.error(`Failed to delete the recipient ${accountHolderRender(params.row.retrieved_values.name, params.row.retrieved_values.nickname)}`)}
          />,
        ]} />
      ),
    },
  ];

  if (checkFlag('paymentsV2')) {
    const verifyCol = {
      field: 'payments_enabled',
      flex: 0.5,
      headerName: (
        <Box display='flex' flexDirection='row'>
          <Typography>Payments Enabled</Typography>
          <Tooltip
            arrow
            title={
              <Typography>
                Payments enabled means you can send funds to this recipient from Payment Enabled bank accounts. This
                will not be enabled for reasons including your recipient has opted out of receiving payments though
                Arpari or the reciepient's bank account credentials are invalid.
              </Typography>
            }
          >
            <InfoOutlinedIcon sx={{ marginLeft: '5px' }} fontSize='large' color='disabled' />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const {
          id,
          dwolla_customer_id,
          dwolla_customer_is_verified,
          dwolla_funding_source_id,
          dwolla_funding_source_is_verified,
        } = params.row
        return (
          <RecipientPaymentsActions
            counterpartyId={id}
            dwollaCustomerId={dwolla_customer_id}
            dwollaCustomerIsVerified={dwolla_customer_is_verified}
            dwollaFundingSourceId={dwolla_funding_source_id}
            dwollaFundingSourceIsVerified={dwolla_funding_source_is_verified}
          />
        )
      },
    }

    // payments col always after bank details column
    const bankDetailsIdx = columns.findIndex((col) => col.field === 'bankDetails')
    const insertIdx = bankDetailsIdx >= 0 ? bankDetailsIdx + 1 : 2
    columns.splice(insertIdx, 0, verifyCol)
  }

  return (
    <TableWithLoadingWrapper
      pagination={{
        paginationModel: { pageSize: state.recipients?.length || 10, page: 0 },
      }}
      pageSizeOptions={[5, 10, 25]}
      columns={columns}
      dataSource={state.recipients}
      tableLayout="fixed"
      loading={props.loading}
      scroll={{
        x: 600,
      }}
      style={{ width: "100%" }}
      getRowId={row => row.id}
    />
  );
}

export default RecipientsTable;